import React, { useState } from 'react'
import { useTheme } from 'styled-components'
import { Box, CheckmarkIcon, Spinner, FlexProps, TextProps } from 'vvs-uikit'

import { StepIndicatorFlex, LineBetweenStep, Circle, StepLabel } from './styles'

/**
 * Widget to indicate steps and where current step is at
 *
 * when `steps` prop is number, steps will shown without label
 * when `steps` is array, its elements will be treated as labels for each step
 * NOTE that `steps` cannot be changed within a `<StepIndicator />` livecycle
 */
const StepIndicator = ({
  steps,
  stepLabelProps,
  currentStep,
  running,
  ...props
}: {
  steps: number | React.ReactNode[]
  stepLabelProps?: TextProps
  currentStep: number
  running?: boolean
} & FlexProps) => {
  const [[stepItems, withLabel]] = useState<[[number, React.ReactNode][], boolean]>(() => {
    if (typeof steps === 'number') {
      return [
        Array(steps)
          .fill(undefined)
          .map((_, i) => [i + 1, '']),
        false,
      ]
    }
    return [steps.map((label, i) => [i + 1, label]), true]
  })

  const lineWidth = `${100 / (stepItems.length + 0.2)}%`
  return (
    <StepIndicatorFlex withLabel={withLabel} {...props}>
      {stepItems.map(([step, label]) => (
        <React.Fragment key={step}>
          <Step
            step={step}
            label={label}
            stepLabelProps={stepLabelProps}
            completed={currentStep > step}
            current={currentStep === step}
            running={running}
          />
          {step < stepItems.length && <LineBetweenStep active={currentStep > step} width={lineWidth} />}
        </React.Fragment>
      ))}
    </StepIndicatorFlex>
  )
}

export default StepIndicator

export { default as StepDots } from './StepDots'

const Step = ({
  step,
  label,
  stepLabelProps,
  completed,
  current,
  running,
}: {
  step: number
  label: React.ReactNode
  stepLabelProps?: TextProps
  completed: boolean
  current: boolean
  running: boolean
}) => {
  const theme = useTheme()
  return (
    <Box position="relative">
      <Circle active={completed}>
        {completed ? <CheckmarkIcon fill="white" /> : current && running ? <Spinner dark={theme.isDark} /> : step}
      </Circle>
      {label && (
        <StepLabel active={current} {...stepLabelProps}>
          {label}
        </StepLabel>
      )}
    </Box>
  )
}
